import {
  findOpenUiBlocks,
  isRecord,
  UI_BLOCK_LIMITS,
  type UiBlockCell,
  type UiBlockColumnType,
} from './ui-block-text'

/**
 * What a stored UI block means (board jzo5): the tree, the canonical OpenUI Lang
 * HUB stores, its strict reader, and markdown. In the barrel and in the first load:
 * when a chunk is gone a stored block still shows its table, and copy needs nothing
 * to arrive first. `bundle-boundary.test.ts` holds its byte budget. No dependency:
 * stored history never depends on OpenUI's code.
 */

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * "1,2", "1.2", "2 450 000", "1 234,5", "1,234,567.8" → number, else null. A
 * single separator is a decimal ("1,234" = 1.234), which is a guess: use this
 * for sorting what is shown as written, never to produce a number that is shown.
 */
export function parseLocaleNumber(raw: string): number | null {
  const text = raw.trim().replace(/\s/g, '').replace(/^[−–]/, '-')
  if (!/^[+-]?\d[\d.,]*$|^[+-]?[.,]\d+$/.test(text)) return null
  const commas = (text.match(/,/g) ?? []).length
  const dots = (text.match(/\./g) ?? []).length
  let normal = text
  if (commas > 0 && dots > 0) {
    const decimal = text.lastIndexOf(',') > text.lastIndexOf('.') ? ',' : '.'
    const group = decimal === ',' ? '.' : ','
    normal = text.split(group).join('').replace(decimal, '.')
  } else if (commas > 1) normal = text.split(',').join('')
  else if (dots > 1) normal = text.split('.').join('')
  else normal = text.replace(',', '.')
  const value = Number(normal)
  return Number.isFinite(value) ? value : null
}

// ---------------------------------------------------------------------------
// The block tree (board jzo5)
// ---------------------------------------------------------------------------

export interface UiColumn {
  label: string
  type?: UiBlockColumnType
  unit?: string
}

export interface UiSeries {
  name: string
  values: (number | null)[]
}

export interface UiDataTable {
  type: 'DataTable'
  columns: UiColumn[]
  rows: UiBlockCell[][]
  title?: string
  note?: string
}

export interface UiChart {
  type: 'Chart'
  kind: 'bar' | 'line' | 'pie'
  categories: string[]
  series: UiSeries[]
  title?: string
  unit?: string
}

export interface UiStat {
  type: 'Stat'
  label: string
  value: string
  delta?: string
  hint?: string
}

export interface UiStack {
  type: 'Stack'
  children: UiNode[]
}

export type UiNode = UiStack | UiDataTable | UiChart | UiStat

// ---------------------------------------------------------------------------
// Canonical OpenUI Lang: what HUB stores
// ---------------------------------------------------------------------------

const str = (value: unknown) => JSON.stringify(value)
const list = (items: string[]) => `[${items.join(', ')}]`

const namedOptions = (entries: [string, string | undefined][]): string[] => {
  const set = entries.filter(([, value]) => value !== undefined)
  return set.length === 0
    ? []
    : [`{${set.map(([key, value]) => `${key}: ${str(value)}`).join(', ')}}`]
}

/** A skipped optional in the middle is `null`, one at the end is left out. */
function positional(args: (string | undefined)[]): string[] {
  let end = args.length
  while (end > 0 && args[end - 1] === undefined) end--
  return args.slice(0, end).map((arg) => (arg === undefined ? 'null' : str(arg)))
}

const column = (c: UiColumn) => `Col(${[str(c.label), ...positional([c.type, c.unit])].join(', ')})`

const series = (s: UiSeries) => `Series(${str(s.name)}, ${str(s.values).replaceAll(',', ', ')})`

const args = (name: string, parts: string[]) => `${name}(${parts.join(', ')})`

/**
 * One statement per node, one line per statement, names in visiting order.
 * Strings are JSON, so a statement never holds a raw newline and no value can
 * close the fence. Values go through verbatim.
 */
export function serializeUiBlock(root: UiStack): string {
  const lines: string[] = []
  let next = 0
  const statement = (node: UiNode): string => {
    const name = node === root ? 'root' : `n${++next}`
    const at = lines.length
    lines.push('')
    lines[at] = `${name} = ${call(node)}`
    return name
  }
  const call = (node: UiNode): string => {
    if (node.type === 'Stack') return args('Stack', [list(node.children.map(statement))])
    if (node.type === 'Stat') {
      return args('Stat', [
        str(node.label),
        str(node.value),
        ...namedOptions([
          ['delta', node.delta],
          ['hint', node.hint],
        ]),
      ])
    }
    if (node.type === 'Chart') {
      return args('Chart', [
        str(node.kind),
        list(node.categories.map(str)),
        list(node.series.map(series)),
        ...namedOptions([
          ['title', node.title],
          ['unit', node.unit],
        ]),
      ])
    }
    return args('DataTable', [
      list(node.columns.map(column)),
      list(node.rows.map((row) => list(row.map(str)))),
      ...namedOptions([
        ['title', node.title],
        ['note', node.note],
      ]),
    ])
  }
  statement(root)
  return lines.join('\n')
}

type Ast =
  string | number | null | Ast[] | { call: string; args: Ast[] } | { ref: string } | AstObject
interface AstObject {
  object: Record<string, Ast>
}

const NUMBER = /-?\d+(\.\d+)?([eE][+-]?\d+)?/y
const NAME = /[A-Za-z_]\w*/y

/** One value of one canonical statement. Throws on anything else. */
function readValue(text: string, at: { i: number }): Ast {
  const peek = () => text[at.i]
  const expect = (ch: string) => {
    if (text[at.i++] !== ch) throw new Error('canonical')
  }
  const items = (close: string, item: () => void) => {
    if (peek() === close) {
      at.i++
      return
    }
    for (;;) {
      item()
      if (peek() !== ',') break
      at.i++
      expect(' ')
    }
    expect(close)
  }
  const first = peek()
  if (first === '"') {
    let end = at.i + 1
    while (text[end] !== '"') {
      if (end >= text.length) throw new Error('canonical')
      end += text[end] === '\\' ? 2 : 1
    }
    const value = JSON.parse(text.slice(at.i, end + 1)) as string
    at.i = end + 1
    return value
  }
  if (first === '[') {
    at.i++
    const out: Ast[] = []
    items(']', () => out.push(readValue(text, at)))
    return out
  }
  if (first === '{') {
    at.i++
    const object: Record<string, Ast> = Object.create(null) as Record<string, Ast>
    items('}', () => {
      NAME.lastIndex = at.i
      const key = NAME.exec(text)?.[0]
      if (!key) throw new Error('canonical')
      at.i += key.length
      expect(':')
      expect(' ')
      object[key] = readValue(text, at)
    })
    return { object }
  }
  NUMBER.lastIndex = at.i
  const number = NUMBER.exec(text)?.[0]
  if (number) {
    at.i += number.length
    return Number(number)
  }
  NAME.lastIndex = at.i
  const name = NAME.exec(text)?.[0]
  if (!name) throw new Error('canonical')
  at.i += name.length
  if (name === 'null') return null
  if (peek() !== '(') return { ref: name }
  at.i++
  const callArgs: Ast[] = []
  items(')', () => callArgs.push(readValue(text, at)))
  return { call: name, args: callArgs }
}

const isCall = (value: Ast, name: string): value is { call: string; args: Ast[] } =>
  typeof value === 'object' && value !== null && 'call' in value && value.call === name

const stringList = (value: Ast): string[] => {
  if (!Array.isArray(value) || !value.every((item) => typeof item === 'string')) {
    throw new Error('canonical')
  }
  return value as string[]
}

/** The named options, by the keys this component has. */
function optionsOf(value: Ast | undefined, keys: string[]): Record<string, string> {
  if (value === undefined) return {}
  if (typeof value !== 'object' || value === null || !('object' in value)) {
    throw new Error('canonical')
  }
  const out: Record<string, string> = {}
  for (const key of keys) {
    const option = value.object[key]
    if (typeof option === 'string') out[key] = option
  }
  return out
}

function readColumn(value: Ast): UiColumn {
  if (!isCall(value, 'Col') || typeof value.args[0] !== 'string') throw new Error('canonical')
  const [label, type, unit] = value.args
  const out: UiColumn = { label: label as string }
  if (type !== undefined && type !== null) out.type = type as UiBlockColumnType
  if (typeof unit === 'string') out.unit = unit
  return out
}

function readSeries(value: Ast): UiSeries {
  if (!isCall(value, 'Series') || typeof value.args[0] !== 'string') throw new Error('canonical')
  if (!Array.isArray(value.args[1])) throw new Error('canonical')
  return { name: value.args[0], values: value.args[1] as (number | null)[] }
}

function readNode(value: Ast, statements: Map<string, Ast>, path: string[]): UiNode {
  if (typeof value !== 'object' || value === null || !('call' in value)) {
    throw new Error('canonical')
  }
  const { call, args: a } = value
  if (call === 'Stack') {
    if (!Array.isArray(a[0])) throw new Error('canonical')
    return {
      type: 'Stack',
      children: a[0].map((child) => {
        if (typeof child !== 'object' || child === null || !('ref' in child)) {
          throw new Error('canonical')
        }
        const target = statements.get(child.ref)
        if (target === undefined || path.includes(child.ref)) throw new Error('canonical')
        return readNode(target, statements, [...path, child.ref])
      }),
    }
  }
  if (call === 'Stat') {
    if (typeof a[0] !== 'string' || typeof a[1] !== 'string') throw new Error('canonical')
    return { type: 'Stat', label: a[0], value: a[1], ...optionsOf(a[2], ['delta', 'hint']) }
  }
  if (call === 'Chart') {
    if (!Array.isArray(a[2])) throw new Error('canonical')
    return {
      type: 'Chart',
      kind: a[0] as UiChart['kind'],
      categories: stringList(a[1] ?? null),
      series: a[2].map(readSeries),
      ...optionsOf(a[3], ['title', 'unit']),
    }
  }
  if (call === 'DataTable') {
    if (!Array.isArray(a[0]) || !Array.isArray(a[1])) throw new Error('canonical')
    return {
      type: 'DataTable',
      columns: a[0].map(readColumn),
      rows: a[1] as UiBlockCell[][],
      ...optionsOf(a[2], ['title', 'note']),
    }
  }
  throw new Error('canonical')
}

/**
 * The strict reader, for what HUB itself stored. It needs no dependency, and
 * nothing it accepts depends on a parser's tolerance: whatever it reads must
 * serialize back to the same text, byte for byte, or it is not canonical and
 * the answer is null. Shape only: the zod rules live in `./ui-blocks`.
 */
export function readCanonicalUiBlock(source: string): UiStack | null {
  if (source.length > UI_BLOCK_LIMITS.specChars * 2) return null
  try {
    const statements = new Map<string, Ast>()
    for (const line of source.split('\n')) {
      const eq = line.indexOf(' = ')
      if (eq <= 0) return null
      const at = { i: eq + 3 }
      const value = readValue(line, at)
      if (at.i !== line.length) return null
      statements.set(line.slice(0, eq), value)
    }
    const root = statements.get('root')
    if (root === undefined || !isCall(root, 'Stack')) return null
    const tree = readNode(root, statements, ['root']) as UiStack
    return serializeUiBlock(tree) === source ? tree : null
  } catch {
    return null
  }
}

/** Deep equality of two trees, whatever order their keys came in. */
export function sameUiValue(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, index) => sameUiValue(item, b[index]))
  }
  if (!isRecord(a) || !isRecord(b)) return false
  const keys = Object.keys(a)
  if (keys.length !== Object.keys(b).length) return false
  return keys.every((key) => Object.hasOwn(b, key) && sameUiValue(a[key], b[key]))
}

// ---------------------------------------------------------------------------
// Markdown
// ---------------------------------------------------------------------------

/** What cannot be written as a cell prints nothing: no `[object Object]`, no `Infinity`. */
function mdCell(value: unknown): string {
  if (typeof value === 'number' && !Number.isFinite(value)) return ''
  if (typeof value === 'object' || value === undefined) return ''
  return String(value)
    .replace(/\s*\n\s*/g, ' ')
    .replace(/\|/g, '\\|')
}

/**
 * Never cuts a row: `["Bygg", 2,500]` under two headers is three cells, and showing
 * the first two would turn 2,500 into 2. Empty headers make room instead. A valid
 * table cannot be wider than its header, so this only ever changes a fallback.
 */
function markdownTable(header: string[], rows: unknown[][]): string {
  const width = rows.reduce((most, row) => Math.max(most, row.length), header.length)
  const columns = Array.from({ length: width }, (_, i) => header[i] ?? '')
  const line = (cells: unknown[]) => `| ${cells.map(mdCell).join(' | ')} |`
  return [
    line(columns),
    line(columns.map(() => '---')),
    ...rows.map((row) => line(columns.map((_, i) => row[i]))),
  ].join('\n')
}

const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null

const columnHeader = (column: unknown): string => {
  if (!isRecord(column)) return mdCell(column)
  const label = text(column.label) ?? ''
  const unit = text(column.unit)
  return unit ? `${label} (${unit})` : label
}

/** `DataTable`-shaped props → title, pipe table, note. Tolerates invalid optionals. */
export function dataTableToMarkdown(props: {
  title?: unknown
  columns?: unknown
  rows?: unknown
  note?: unknown
}): string | null {
  if (!Array.isArray(props.columns) || props.columns.length === 0) return null
  if (!Array.isArray(props.rows)) return null
  const rows = props.rows
    .filter((r): r is unknown[] => Array.isArray(r))
    .slice(0, UI_BLOCK_LIMITS.rows)
  const parts: string[] = []
  const title = text(props.title)
  if (title) parts.push(`**${title}**`)
  parts.push(markdownTable(props.columns.map(columnHeader), rows))
  const note = text(props.note)
  if (note) parts.push(`_${note}_`)
  return parts.join('\n\n')
}

export function chartToMarkdown(props: Record<string, unknown>): string | null {
  if (!Array.isArray(props.categories) || !Array.isArray(props.series)) return null
  const series = props.series.filter(isRecord)
  if (series.length === 0) return null
  const unit = text(props.unit)
  const names = series.map((s) => {
    const name = text(s.name) ?? ''
    return unit ? `${name} (${unit})` : name
  })
  const values = series.map((s) => (Array.isArray(s.values) ? (s.values as unknown[]) : []))
  // A series longer than the categories keeps its values, under an empty category.
  const length = values.reduce((most, v) => Math.max(most, v.length), props.categories.length)
  const rows = Array.from({ length }, (_, i) => [
    (props.categories as unknown[])[i],
    ...values.map((v) => v[i]),
  ])
  const title = text(props.title)
  const table = markdownTable(['', ...names], rows)
  return title ? `**${title}**\n\n${table}` : table
}

export function statToMarkdown(props: Record<string, unknown>): string | null {
  const label = text(props.label)
  const value = text(props.value) ?? (typeof props.value === 'number' ? String(props.value) : null)
  if (!label || !value) return null
  const delta = text(props.delta)
  const hint = text(props.hint)
  return `**${label}:** ${value}${delta ? ` (${delta})` : ''}${hint ? ` — ${hint}` : ''}`
}

/** The readable version of a block tree: tables and bold lines, '' when it holds nothing. */
export function uiNodeToMarkdown(node: UiNode): string {
  if (node.type === 'Stack') {
    return node.children.map(uiNodeToMarkdown).filter(Boolean).join('\n\n')
  }
  if (node.type === 'DataTable') return dataTableToMarkdown({ ...node }) ?? ''
  if (node.type === 'Chart') return chartToMarkdown({ ...node }) ?? ''
  return statToMarkdown({ ...node }) ?? ''
}

/** Pipe-table cells and bold lines from `content`'s stored blocks. */
export function uiBlocksToMarkdown(content: string, options?: { unrenderable?: string }): string {
  const blocks = findOpenUiBlocks(content)
  if (blocks.length === 0) return content
  let out = ''
  let at = 0
  for (const block of blocks) {
    out += content.slice(at, block.start)
    const tree = readCanonicalUiBlock(block.source)
    out += (tree && uiNodeToMarkdown(tree)) || (options?.unrenderable ?? '')
    at = block.end
  }
  return (out + content.slice(at)).replace(/\n{3,}/g, '\n\n').trimEnd()
}
